import { Skeleton } from "@/components/ui/Skeleton";
import {
  RiPlaneLine,
  RiMapPinLine,
  RiPhoneLine,
  RiPrinterLine,
  RiFlightTakeoffLine,
  RiFlightLandLine,
  RiUserLine,
  RiArrowRightLine,
  RiTimeLine,
  RiRestaurantLine,
  RiCarLine,
  RiAddLine,
  RiCloseLine,
  RiCalendarLine,
  RiSunLine,
} from "@remixicon/react";
import { resolveAssetUrl } from "@/utils/urls";
import AircraftMediaPreview from "./AircraftMediaPreview";
import AircraftImage from "@/features/tails/components/AircraftImage";
// Service-category icons — one per category in AddServiceModal's
// SERVICE_CATEGORIES list. Keyed by the category name string.
import icTransportation from "@/assets/images/service-icons/ic-transportation.svg";
import icCatering from "@/assets/images/service-icons/ic-catering.svg";
import icBeverage from "@/assets/images/service-icons/ic-beverage.svg";
import icPassengerServices from "@/assets/images/service-icons/ic-passenger-services.svg";
import icWellness from "@/assets/images/service-icons/ic-wellness.svg";
import icSecurity from "@/assets/images/service-icons/ic-security.svg";
import icConcierge from "@/assets/images/service-icons/ic-concierge.svg";
import icTripProtection from "@/assets/images/service-icons/ic-trip-protection.svg";

// Inline editable input that blends with the document
// Auto-grow a textarea to fit its content so long values wrap onto new lines.
const autoGrow = (el) => {
  if (!el) return;
  el.style.height = "auto";
  el.style.height = `${el.scrollHeight}px`;
};

const Field = ({
  value,
  onChange,
  placeholder,
  className = "",
  inputClassName = "",
  readOnly = false,
  multiline = false,
}) =>
  readOnly ? (
    <span className={`block ${multiline ? "whitespace-pre-wrap break-words" : ""} ${className}`}>{value || placeholder}</span>
  ) : multiline ? (
    <textarea
      ref={autoGrow}
      rows={1}
      value={value || ""}
      onChange={(e) => { onChange(e.target.value); autoGrow(e.target); }}
      placeholder={placeholder}
      className={`block w-full bg-transparent outline-none resize-none overflow-hidden break-words transition-colors border-b border-dashed border-gray-300 focus:border-primary-400 ${inputClassName} ${className}`}
    />
  ) : (
    <input
      type="text"
      value={value || ""}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className={`bg-transparent outline-none transition-colors border-b border-dashed border-gray-300 focus:border-primary-400 ${inputClassName} ${className}`}
    />
  );

// Field for white-on-dark sections
const LightField = ({ value, onChange, placeholder, className = "", readOnly = false }) =>
  readOnly ? (
    <span className={`text-white ${className}`}>{value || placeholder}</span>
  ) : (
    <input
      type="text"
      value={value || ""}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className={`bg-transparent outline-none text-white transition-colors border-b border-dashed border-white/30 focus:border-white/60 placeholder:text-white/80 ${className}`}
    />
  );

const SectionLabel = ({ children, onAdd, addLabel }) => (
  <div className="flex items-center justify-between mb-2">
    <span className="text-white/70 text-xs font-semibold uppercase tracking-wider">
      {children}
    </span>
    {onAdd && (
      <button
        onClick={onAdd}
        className="no-print flex items-center gap-1 text-white/80 hover:text-white/80 transition-colors"
      >
        <RiAddLine className="w-3.5 h-3.5" />
        <span className="text-[10px] tracking-normal font-medium">
          {addLabel}
        </span>
      </button>
    )}
  </div>
);

const WeatherFields = ({ data, basePath, onChange, brandColor }) => (
  <div className="flex items-center gap-2 text-xs">
    <RiSunLine
      className={`w-4 h-4 shrink-0 ${brandColor ? '' : 'text-primary-500'}`}
      style={brandColor ? { color: brandColor } : undefined}
    />
    <Field
      value={data.weather}
      onChange={(v) => onChange(`${basePath}.weather`, v)}
      placeholder="Weather"
      className="text-xs text-gray-600 flex-1"
    />
    <span className="text-gray-400">|</span>
    <Field
      value={data.tempHi}
      onChange={(v) => onChange(`${basePath}.tempHi`, v)}
      placeholder="Hi"
      className="text-xs text-gray-600 w-10"
    />
    <Field
      value={data.tempLo}
      onChange={(v) => onChange(`${basePath}.tempLo`, v)}
      placeholder="Lo"
      className="text-xs text-gray-600 w-10"
    />
  </div>
);

const AirportFields = ({ title, data, basePath, onChange, brandColor, readOnly = false }) => (
  <div className="space-y-2">
    <div>
      <p className="text-[10px] uppercase text-gray-400 tracking-wider mb-2">
        {title}
      </p>
      <Field
        value={data.airportName}
        onChange={(v) => onChange(`${basePath}.airportName`, v)}
        placeholder="Airport Name"
        className="text-sm font-bold text-gray-900 w-full"
        readOnly={readOnly}
      />
      {(data.fbo !== undefined && data.fbo !== null) && (
        <Field
          value={data.fbo}
          onChange={(v) => onChange(`${basePath}.fbo`, v)}
          placeholder="FBO"
          multiline
          className="text-xs font-medium text-gray-700 mt-1 w-full"
          readOnly={readOnly}
        />
      )}
      <Field
        value={data.address}
        onChange={(v) => onChange(`${basePath}.address`, v)}
        placeholder="Airport Address"
        multiline
        className="text-xs text-gray-500 mt-1 w-full"
        readOnly={readOnly}
      />
    </div>
    {/* <div className="flex items-center gap-1.5 text-xs text-gray-600">
      <RiPhoneLine className="w-3 h-3 shrink-0" />
      <Field
        value={data.contactNumber}
        onChange={(v) => onChange(`${basePath}.contactNumber`, v)}
        placeholder="Contact Number"
        className="text-xs w-full"
      />
    </div> */}
    {/* <div>
      <WeatherFields
        data={data}
        basePath={basePath}
        onChange={onChange}
        brandColor={brandColor}
      />
    </div> */}
  </div>
);

// Map service categories → their custom SVG icon URLs. Keys match the
// `name` strings in AddServiceModal's SERVICE_CATEGORIES exactly, so
// looking up `serviceIcons[service.name]` returns the right SVG.
const serviceIcons = {
  "Transportation": icTransportation,
  "Catering": icCatering,
  "Beverage & Bar": icBeverage,
  "Passenger Services": icPassengerServices,
  "Wellness & Medical": icWellness,
  "Security": icSecurity,
  "Concierge & Documents": icConcierge,
  "Trip Protection": icTripProtection,
};

const TripSheetDocument = ({
  data,
  organization,
  isOrgLoading,
  onChange,
  onAddPassenger,
  onRemovePassenger,
  onAddCrew,
  onRemoveCrew,
  onAddService,
  onRemoveService,
  protectedCounts,
  fetchingArrivalTimes,
}) => {
  const brandColor = organization?.branding?.color;

  return (
    <div className="w-full max-w-[210mm] mx-auto text-gray-900 font-rethink print:w-full p-2 md:p-4 space-y-3">
      {/* ===== HEADER ===== */}
      <div className="bg-white rounded-xl px-4 md:px-5 py-4 flex flex-col md:flex-row items-start justify-between gap-3">
        <div
          className="flex items-center justify-center shrink-0 rounded-lg overflow-hidden"
          style={{
            backgroundColor: brandColor || '#f3f4f6',
            width: 120,
            height: 80,
          }}
        >
          {isOrgLoading ? (
            <Skeleton className="h-full w-full" />
          ) : organization?.branding?.logo ? (
            <img src={resolveAssetUrl(organization.branding.logo)} alt={organization.name || "Logo"} className="w-full h-full object-contain p-1.5" />
          ) : (
            <div className="flex items-center gap-1">
              <img src="/images/icons/planeright.svg" alt="Logo" className="w-8 h-8" />
              <span className="text-2xl font-bold text-white">Logo</span>
            </div>
          )}
        </div>
        <div className="text-left md:text-right text-md space-y-0.5 w-full md:w-auto">
          <Field
            value={data.operator.name || "-"}
            onChange={(v) => onChange("operator.name", v)}
            placeholder="[Brokerage Company Name]"
            className="font-bold text-lg text-gray-900 text-left md:text-right w-full"
            readOnly
          />
          <div className="flex items-center justify-start md:justify-end gap-1.5 text-gray-600">
            <RiMapPinLine className="w-3.5 h-3.5 shrink-0" />
            <Field
              value={data.operator.address || "-"}
              onChange={(v) => onChange("operator.address", v)}
              placeholder="[Brokerage Address]"
              className="text-left md:text-right w-full"
              readOnly
            />
          </div>
          <div className="flex items-center justify-start md:justify-end gap-1.5 text-gray-600">
            <RiPhoneLine className="w-3.5 h-3.5 shrink-0" />
            <Field
              value={data.operator.phone || "-"}
              onChange={(v) => onChange("operator.phone", v)}
              placeholder="[Brokerage Phone Number]"
              className="text-left md:text-right w-full"
              readOnly
            />
          </div>
          <div className="flex items-center justify-start md:justify-end gap-1.5 text-gray-600">
            <RiPrinterLine className="w-3.5 h-3.5 shrink-0" />
            <Field
              value={data.operator.fax || "-"}
              onChange={(v) => onChange("operator.fax", v)}
              placeholder="[Brokerage Fax Number]"
              className="text-left md:text-right w-full"
              readOnly
            />
          </div>
        </div>
      </div>

      {/* ===== CLIENT BAR ===== */}
      <div className={`rounded-xl px-3 md:px-4 py-2.5 flex flex-wrap md:flex-nowrap items-center gap-2 md:gap-3 text-sm ${brandColor ? '' : 'bg-primary-500'}`} style={brandColor ? { backgroundColor: brandColor } : undefined}>
        <LightField
          value={data.client.name || "-"}
          onChange={(v) => onChange("client.name", v)}
          placeholder="[Client name]"
          className="font-medium flex-1 min-w-0 text-xs"
          readOnly
        />
        <LightField
          value={data.client.company || "-"}
          onChange={(v) => onChange("client.company", v)}
          placeholder="[Client company name]"
          className="flex-1 min-w-0 text-xs"
          readOnly
        />
        <LightField
          value={data.client.email || "-"}
          onChange={(v) => onChange("client.email", v)}
          placeholder="[Client email]"
          className="flex-[1.3] min-w-0 text-xs"
          readOnly
        />
        <LightField
          value={data.client.phone || "-"}
          onChange={(v) => onChange("client.phone", v)}
          placeholder="[Client phone number]"
          className="flex-1 min-w-0 text-xs"
          readOnly
        />
      </div>

      <div className={`rounded-xl p-4 space-y-3 overflow-hidden ${brandColor ? '' : 'bg-primary-500'}`} style={brandColor ? { backgroundColor: brandColor } : undefined}>

      {/* ===== LEG SUMMARY =====
          Aircraft panel narrowed from 300px→240px and the summary panel
          gets min-w-0 so it can shrink and reflow. At A4 preview width
          (~729px inner), 300px was too greedy — the summary row's
          departure/arrival groups couldn't fit and overshot the blue
          container edge. */}
      <div className="grid grid-cols-1 md:grid-cols-[240px_minmax(0,1fr)] gap-3">
        {/* Aircraft Panel — matches PDF's aircraftPanel/aircraftImage/aircraftInfo
            structure: image goes edge-to-edge at the top (no ring of
            padding), then the info block below gets its own inner padding.
            Image height 110pt ≈ 147px at A4 preview scale, matching what
            the PDF shows visually. */}
        <div className="bg-white rounded-xl overflow-hidden flex flex-col justify-between">
          <div className="bg-gray-100 h-[147px] flex items-center justify-center">
            <AircraftImage
              aircraft={data.aircraft}
              src={data.aircraft.image}
              alt={data.aircraft.name}
              className="w-full h-full object-cover"
            />
          </div>
          <div className="p-3 overflow-hidden">
            <Field
              value={data.aircraft.tailNumber}
              onChange={(v) => onChange("aircraft.tailNumber", v)}
              placeholder="Tail Number"
              className="text-xl font-bold w-full text-gray-900"
              readOnly
            />
            <div className="flex items-center gap-2 text-xs text-gray-500 mt-1">
              <span className="flex items-center gap-1">
                <RiPlaneLine className="w-3 h-3 shrink-0" />
                <Field
                  value={data.aircraft.name}
                  onChange={(v) => onChange("aircraft.name", v)}
                  placeholder="Aircraft Name"
                  className="text-xs w-auto"
                  readOnly
                />
              </span>
              <span className="flex items-center gap-1">
                <RiUserLine className="w-3 h-3 shrink-0" />
                <Field
                  value={String(data.aircraft.passengerCount)}
                  onChange={(v) => onChange("aircraft.passengerCount", v)}
                  placeholder="0"
                  className="text-xs"
                  readOnly
                />
              </span>
            </div>

            {/* Aircraft Note — same amber-callout styling used on the
                itinerary email and QuoteDetailsDrawer so this reads as
                the same note across surfaces. Renders only when set. */}
            {data.aircraft.note && (
              <div className="mt-3 p-2.5 bg-amber-50 border border-amber-100 rounded-lg">
                <h4 className="text-[9px] font-semibold text-amber-900 uppercase tracking-wider mb-1">Aircraft Note</h4>
                <p className="text-[11px] text-amber-800 whitespace-pre-wrap leading-snug">{data.aircraft.note}</p>
              </div>
            )}
          </div>
        </div>

        {/* Leg Summary Table — matches PDF layout: panel padding 12pt (p-4),
            title marginBottom 6pt (mb-2), each leg on ONE row with LEG label
            inline (not stacked above). Font sizes tuned to PDF proportions
            at A4 preview width. */}
        <div className="bg-white rounded-xl p-4">
          <div className="flex items-center justify-between mb-2">
            <h3 className="font-bold text-sm">Leg Summary</h3>
          </div>
          <div className="space-y-2">
            {data.legs.map((leg, idx) => (
              <div key={idx} className="text-[10px] text-gray-600 flex items-center justify-between flex-nowrap gap-1">
                <span className="font-semibold text-gray-900 text-[11px] shrink-0">
                  LEG {leg.legNumber}
                </span>
                {/* Departure code pill */}
                <span className="flex items-center gap-1 bg-gray-100 rounded-md px-1.5 py-0.5 shrink-0">
                  <RiFlightTakeoffLine className={`w-2.5 h-2.5 shrink-0 ${brandColor ? '' : 'text-primary-500'}`} style={brandColor ? { color: brandColor } : undefined} />
                  <Field value={leg.departure.code} onChange={(v) => onChange(`legs.${idx}.departure.code`, v)} placeholder="CODE" className="text-[10px] font-bold whitespace-nowrap" readOnly />
                </span>
                {/* Departure date */}
                <span className="flex items-center gap-0.5 shrink-0">
                  <RiCalendarLine className="w-2.5 h-2.5 text-gray-400 shrink-0" />
                  <Field value={leg.departure.date} onChange={(v) => onChange(`legs.${idx}.departure.date`, v)} placeholder="-" className="text-[10px] whitespace-nowrap" readOnly />
                </span>
                {/* Departure time */}
                <span className="flex items-center gap-0.5 shrink-0">
                  <RiTimeLine className="w-2.5 h-2.5 text-gray-400 shrink-0" />
                  <Field value={leg.departure.time} onChange={(v) => onChange(`legs.${idx}.departure.time`, v)} placeholder="-" className="text-[10px] whitespace-nowrap" readOnly />
                </span>
                <RiArrowRightLine className="w-2.5 h-2.5 text-gray-700 shrink-0" />
                {/* Arrival code pill */}
                <span className="flex items-center gap-1 bg-gray-100 rounded-md px-1.5 py-0.5 shrink-0">
                  <RiFlightLandLine className={`w-2.5 h-2.5 shrink-0 ${brandColor ? '' : 'text-primary-500'}`} style={brandColor ? { color: brandColor } : undefined} />
                  <Field value={leg.arrival.code} onChange={(v) => onChange(`legs.${idx}.arrival.code`, v)} placeholder="CODE" className="text-[10px] font-bold whitespace-nowrap" readOnly />
                </span>
                {/* Arrival date */}
                <span className="flex items-center gap-0.5 shrink-0">
                  <RiCalendarLine className="w-2.5 h-2.5 text-gray-400 shrink-0" />
                  <Field value={leg.arrival.date} onChange={(v) => onChange(`legs.${idx}.arrival.date`, v)} placeholder="-" className="text-[10px] whitespace-nowrap" readOnly />
                </span>
                {/* Arrival time */}
                <span className="flex items-center gap-0.5 shrink-0">
                  <RiTimeLine className="w-2.5 h-2.5 text-gray-400 shrink-0" />
                  {fetchingArrivalTimes ? (
                    <Skeleton className="h-3 w-10 rounded" />
                  ) : (
                    <Field
                      value={leg.arrival.time}
                      onChange={(v) => onChange(`legs.${idx}.arrival.time`, v)}
                      placeholder="-"
                      className="text-[10px] whitespace-nowrap"
                      readOnly
                    />
                  )}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ===== LEG DETAILS ===== */}
      {data.legs.map((leg, idx) => (
        <div key={idx} className="space-y-3">
          {idx > 0 && (
            <div className="py-4"><div className="border-t-2 border-dashed border-white/30" /></div>
          )}
          <div className="flex items-center justify-between mb-2">
            <span className="text-white/70 text-xs font-semibold uppercase tracking-wider">
              Leg {leg.legNumber}
            </span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-[1.4fr_1fr_1fr] gap-3">
            {/* Left: Codes & Times */}
            <div className="bg-white rounded-xl p-3 md:p-4 min-w-0 overflow-hidden">
              <div className="flex items-center justify-between text-[10px] uppercase tracking-wider text-gray-400 gap-1">
                {fetchingArrivalTimes ? (
                  <Skeleton className="h-3 w-10 rounded" />
                ) : (
                  <Field
                    value={leg.timezone}
                    onChange={(v) => onChange(`legs.${idx}.timezone`, v)}
                    placeholder="TZ"
                    className="text-[10px] font-medium w-auto"
                    readOnly
                  />
                )}
                {fetchingArrivalTimes ? (
                  <Skeleton className="h-3 w-14 rounded" />
                ) : (
                  <Field
                    value={leg.flightDistance}
                    onChange={(v) => onChange(`legs.${idx}.flightDistance`, v)}
                    placeholder="Distance"
                    className="text-[10px] font-medium w-auto text-right flex-shrink-0"
                    readOnly
                  />
                )}
              </div>
              <div className="flex items-center gap-2 mt-3">
                <Field value={leg.departure.code} onChange={(v) => onChange(`legs.${idx}.departure.code`, v)} placeholder="CODE" className="text-xl md:text-2xl font-black text-gray-900 w-16 md:w-20" readOnly />
                <div className="flex-1 flex items-center gap-1 min-w-0">
                  <div className="flex-1 border-t border-gray-300"></div>
                  <RiPlaneLine className={`w-4 h-4 md:w-5 md:h-5 flex-shrink-0 ${brandColor ? '' : 'text-primary-500'}`} style={{ transform: "rotate(90deg)", ...(brandColor ? { color: brandColor } : {}) }} />
                  <div className="flex-1 border-t border-gray-300"></div>
                </div>
                <Field value={leg.arrival.code} onChange={(v) => onChange(`legs.${idx}.arrival.code`, v)} placeholder="CODE" className="text-xl md:text-2xl font-black text-gray-900 w-16 md:w-20 text-right" readOnly />
              </div>
              <div className="grid grid-cols-3 gap-2 mt-4 text-xs text-gray-600">
                <div>
                  <Field value={leg.departure.time} onChange={(v) => onChange(`legs.${idx}.departure.time`, v)} placeholder="Time" className="text-sm font-bold text-gray-900 w-full" readOnly />
                  <Field value={leg.departure.date} onChange={(v) => onChange(`legs.${idx}.departure.date`, v)} placeholder="Date" className="text-xs text-gray-500 mt-0.5 w-full" readOnly />
                  <Field value={leg.departure.day} onChange={(v) => onChange(`legs.${idx}.departure.day`, v)} placeholder="Day" className="text-xs text-gray-500 w-full" readOnly />
                </div>
                <div className="text-center flex items-start justify-center pt-0.5">
                  {fetchingArrivalTimes ? (
                    <Skeleton className="h-3 w-12 rounded" />
                  ) : (
                    <Field
                      value={leg.flightDuration}
                      onChange={(v) => onChange(`legs.${idx}.flightDuration`, v)}
                      placeholder="Duration"
                      className="text-xs font-medium text-gray-500 w-full text-center"
                      readOnly
                    />
                  )}
                </div>
                <div className="text-right">
                  {fetchingArrivalTimes ? (
                    <Skeleton className="h-4 w-14 rounded ml-auto" />
                  ) : (
                    <Field
                      value={leg.arrival.time}
                      onChange={(v) => onChange(`legs.${idx}.arrival.time`, v)}
                      placeholder="-"
                      className="text-sm font-bold text-gray-900 w-full text-right"
                      readOnly
                    />
                  )}
                  {fetchingArrivalTimes ? (
                    <Skeleton className="h-3 w-16 rounded ml-auto mt-0.5" />
                  ) : (
                    <Field value={leg.arrival.date} onChange={(v) => onChange(`legs.${idx}.arrival.date`, v)} placeholder="Date" className="text-xs text-gray-500 mt-0.5 w-full text-right" readOnly />
                  )}
                  {fetchingArrivalTimes ? (
                    <Skeleton className="h-3 w-12 rounded ml-auto" />
                  ) : (
                    <Field value={leg.arrival.day} onChange={(v) => onChange(`legs.${idx}.arrival.day`, v)} placeholder="Day" className="text-xs text-gray-500 w-full text-right" readOnly />
                  )}
                </div>
              </div>
            </div>

            {/* Center: Departure Airport */}
            <div className="bg-white rounded-xl p-4 min-w-0 overflow-hidden">
              <AirportFields
                title="Departure"
                data={leg.departure}
                basePath={`legs.${idx}.departure`}
                onChange={onChange}
                brandColor={brandColor}
                readOnly
              />
            </div>

            {/* Right: Arrival Airport */}
            <div className="bg-white rounded-xl p-4 min-w-0 overflow-hidden">
              <AirportFields
                title="Arrival"
                data={leg.arrival}
                basePath={`legs.${idx}.arrival`}
                onChange={onChange}
                brandColor={brandColor}
                readOnly
              />
            </div>
          </div>

      {/* ===== CREW & PASSENGERS (& NOTE) for this leg =====
          Grid keeps the Note pinned at 25% (col-span-1 of 4) — the
          Passengers card grows into Crew's slot when Crew is empty,
          so the Note doesn't drift wider than 1/4 of the row.
            · notes + crew + pass → 4 cols: crew(1) / pass(2) / note(1)  → 25/50/25
            · notes + pass only   → 4 cols: pass(3) / note(1)             → 75/25
            · notes + crew only   → 4 cols: crew(3) / note(1)             → 75/25
            · notes only          → 4 cols: note(4)                       → 100%
            · no notes            → original 50/50 (or single-card + spacer)
          Row hides only when NOTHING to show. */}
      {(leg.crew.length > 0 || leg.passengers.length > 0 || leg.notes) && (
      <div className={`grid grid-cols-1 gap-3 ${leg.notes
        ? 'md:grid-cols-4'
        : 'md:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]'
      }`}>
        {/* Crew. col-span-1 when notes are present (25% of row) — the
            inner chip grid drops to 1-col at that width so long names
            don't squeeze. col-span-3 when only crew + notes remain, so
            the crew card grows to fill the leftover width. */}
        {leg.crew.length > 0 && (
        <div className={`flex flex-col ${leg.notes
          ? (leg.passengers.length > 0 ? 'md:col-span-1' : 'md:col-span-3')
          : ''}`}>
          <SectionLabel addLabel="Add Crew">
            Crew
          </SectionLabel>
          <div className="bg-white rounded-xl p-3 md:p-4 flex-1">
            <div className={`grid gap-3 ${leg.notes && leg.passengers.length > 0
              ? 'grid-cols-1'          /* Crew col at 25% → single chip column */
              : 'grid-cols-1 md:grid-cols-2' /* wider Crew col → 2 chips per row */
            }`}>
              {leg.crew.map((member, i) => {
                const isProtected = i < (protectedCounts?.legs?.[idx]?.crew ?? 0);
                return (
                <div key={i} className="flex items-start gap-1 group bg-gray-100 p-1 rounded-md">
                  <div className="flex-1">
                    <Field
                      value={member.name}
                      onChange={(v) => onChange(`legs.${idx}.crew.${i}.name`, v)}
                      placeholder={`Crew Name ${i + 1}`}
                      className="text-sm font-semibold text-gray-900 w-full"
                      readOnly
                    />
                    <Field
                      value={member.phone}
                      onChange={(v) => onChange(`legs.${idx}.crew.${i}.phone`, v)}
                      placeholder="Phone Number"
                      className="text-xs text-gray-500 w-full"
                      readOnly
                    />
                  </div>
                  {!isProtected && (
                  <button
                    onClick={() => onRemoveCrew(idx, i)}
                    className="no-print opacity-0 group-hover:opacity-100 text-gray-400 hover:text-red-500 transition-all mt-1 shrink-0"
                    title="Remove crew member"
                  >
                    <RiCloseLine className="w-3.5 h-3.5" />
                  </button>
                  )}
                </div>
                );
              })}
            </div>
          </div>
        </div>
        )}

        {/* Passengers. When notes are present: col-span-2 with crew
            (25/50/25), col-span-3 when crew is absent (75/25). Note
            stays pinned at col-span-1 so it never balloons past 25%. */}
        {leg.passengers.length > 0 && (
        <div className={`flex flex-col ${leg.notes
          ? (leg.crew.length > 0 ? 'md:col-span-2' : 'md:col-span-3')
          : ''}`}>
          <SectionLabel addLabel="Add Passenger">
            Passengers
          </SectionLabel>
          <div className="bg-white rounded-xl p-3 md:p-4 flex-1">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {leg.passengers.map((passenger, i) => {
                const isProtected = i < (protectedCounts?.legs?.[idx]?.passengers ?? 0);
                return (
                <div
                  key={i}
                  className="bg-gray-100 rounded-md px-2.5 py-1.5 flex items-center gap-1 group"
                >
                  <Field
                    value={typeof passenger === "object" ? passenger.fullName || "" : passenger}
                    onChange={(v) => {
                      const updated = [...leg.passengers];
                      updated[i] = typeof passenger === "object" ? { ...passenger, fullName: v } : v;
                      onChange(`legs.${idx}.passengers`, updated);
                    }}
                    placeholder={`Passenger Name ${i + 1}`}
                    className="text-sm w-full border-none"
                    readOnly
                  />
                  {!isProtected && (
                  <button
                    onClick={() => onRemovePassenger(idx, i)}
                    className="no-print opacity-0 group-hover:opacity-100 text-gray-400 hover:text-red-500 transition-all shrink-0"
                    title="Remove passenger"
                  >
                    <RiCloseLine className="w-3.5 h-3.5" />
                  </button>
                  )}
                </div>
                );
              })}
            </div>
          </div>
        </div>
        )}

        {/* Note. Pinned at col-span-1 (25% of the 4-col grid) whenever
            crew or passengers accompany it, so the note never widens
            past its slot. When it's the only card in the row, expand
            to col-span-4 so it doesn't sit at 25% with 75% empty. */}
        {leg.notes && (
        <div className={`flex flex-col ${
          leg.crew.length === 0 && leg.passengers.length === 0
            ? 'md:col-span-4'
            : 'md:col-span-1'
        }`}>
          <SectionLabel>Note</SectionLabel>
          <div className="bg-white rounded-xl p-3 md:p-4 flex-1">
            <div className="bg-gray-100 rounded-md px-2.5 py-1.5">
              <p className="text-sm text-gray-700 leading-relaxed whitespace-pre-wrap">
                {leg.notes}
              </p>
            </div>
          </div>
        </div>
        )}
      </div>
      )}

      {/* ===== ADDITIONAL SERVICES for this leg ===== */}
      {leg.additionalServices.length > 0 && (
      <div>
        <SectionLabel addLabel="Add Service">
          Additional Services
        </SectionLabel>
        <div className="bg-white rounded-xl overflow-x-auto">
          <table className="w-full text-sm min-w-[400px]">
            <colgroup>
              <col style={{ width: '25%' }} />
              <col style={{ width: '75%' }} />
              <col className="no-print" />
            </colgroup>
            <thead>
              <tr className="text-xs text-gray-500 uppercase tracking-wider">
                <th className="text-left px-3 py-2 font-medium">Name</th>
                <th className="text-left px-3 py-2 font-medium">
                  Service Details
                </th>
                {/* <th className="text-left px-3 py-2 font-medium">Address</th> */}
                {/* <th className="text-left px-3 py-2 font-medium">
                  Phone Number
                </th> */}
                {/* <th className="text-left px-3 py-2 font-medium">
                  Confirmation Number
                </th> */}
                <th className="no-print w-8"></th>
              </tr>
            </thead>
            <tbody>
              {leg.additionalServices.map((service, i) => {
                // Look up the custom category icon by the service name.
                // Fallback: the plane icon rendered as inline React component.
                const iconSrc = serviceIcons[service.name];
                const isServiceProtected = i < (protectedCounts?.legs?.[idx]?.services ?? 0);
                return (
                  <tr
                    key={i}
                    className="border-b border-gray-100 last:border-0 group"
                  >
                    <td className="px-3 py-2.5">
                      <div className="flex items-center gap-2">
                        {iconSrc ? (
                          <img src={iconSrc} alt="" className="w-4 h-4 shrink-0" />
                        ) : (
                          <RiPlaneLine className={`w-4 h-4 shrink-0 ${brandColor ? '' : 'text-primary-500'}`} style={brandColor ? { color: brandColor } : undefined} />
                        )}
                        <Field
                          value={service.name}
                          onChange={(v) =>
                            onChange(`legs.${idx}.additionalServices.${i}.name`, v)
                          }
                          placeholder="Service Name"
                          className="text-sm font-medium w-full"
                          readOnly
                        />
                      </div>
                    </td>
                    <td className="px-3 py-2.5">
                      <Field
                        value={service.serviceDetails}
                        onChange={(v) =>
                          onChange(`legs.${idx}.additionalServices.${i}.serviceDetails`, v)
                        }
                        placeholder="Service Details"
                        className="text-sm text-gray-600 w-full"
                        readOnly
                      />
                    </td>
                    {/* <td className="px-3 py-2.5">
                      <Field
                        value={service.address}
                        onChange={(v) =>
                          onChange(`legs.${idx}.additionalServices.${i}.address`, v)
                        }
                        placeholder="Address"
                        className="text-sm text-gray-600 w-full"
                        readOnly
                      />
                    </td>
                    <td className="px-3 py-2.5">
                      <Field
                        value={service.phone}
                        onChange={(v) =>
                          onChange(`legs.${idx}.additionalServices.${i}.phone`, v)
                        }
                        placeholder="Phone"
                        className="text-sm text-gray-600 w-full"
                        readOnly
                      />
                    </td>
                    <td className="px-3 py-2.5">
                      <Field
                        value={service.confirmationNumber}
                        onChange={(v) =>
                          onChange(
                            `legs.${idx}.additionalServices.${i}.confirmationNumber`,
                            v
                          )
                        }
                        placeholder="Confirmation #"
                        className="text-sm text-gray-600 w-full"
                        readOnly
                      />
                    </td> */}
                    <td className="px-1 py-2.5">
                      {i >= (protectedCounts?.legs?.[idx]?.services ?? 0) && (
                      <button
                        onClick={() => onRemoveService(idx, i)}
                        className="no-print opacity-0 group-hover:opacity-100 text-gray-400 hover:text-red-500 transition-all"
                        title="Remove service"
                      >
                        <RiCloseLine className="w-3.5 h-3.5" />
                      </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {/* <button
            onClick={() => onAddService(idx)}
            className={`no-print w-full border-t border-dashed border-gray-300 py-2 flex items-center justify-center gap-1.5 text-xs text-gray-400 transition-colors ${brandColor ? '' : 'hover:border-primary-400 hover:text-primary-500'}`}
              onMouseEnter={brandColor ? (e) => { e.currentTarget.style.color = brandColor; e.currentTarget.style.borderColor = brandColor; } : undefined}
              onMouseLeave={brandColor ? (e) => { e.currentTarget.style.color = ''; e.currentTarget.style.borderColor = ''; } : undefined}
          >
            <RiAddLine className="w-3.5 h-3.5" />
            Add Service
          </button> */}
        </div>
      </div>
      )}
        </div>
      ))}

      {/* Trailing aircraft-media row — route map + exterior + interior.
          Mirrors the last block of the generated PDF (TripSheetPdf.jsx). */}
      <AircraftMediaPreview data={data} />

      </div>

      {/* ===== FOOTER ===== */}
      <div className="text-center text-xs text-white/40 pb-2">
        Page 1 of 2
      </div>
    </div>
  );
};

export default TripSheetDocument;